import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import InnerLayout from "../../../components/layouts/inner-layout";
import AddButton, { DraftButton } from "../../../components/buttons/button";
import SqlEditor from "../../../components/custom-reports/sql-editor";
import SchemaBrowser from "../../../components/custom-reports/schema-browser";
import CustomReportTable from "../../../components/custom-reports/custom-report-table";
import ExportDropdown from "../../../components/custom-reports/export-dropdown";
import SaveReportModal from "../../../components/custom-reports/save-report-modal";
import FunctionEditorModal from "../../../components/custom-reports/function-editor-modal";
import FilterConfigModal from "../../../components/custom-reports/filter-config-modal";
import {
  customReportSchemaAction,
  customReportExecuteAction,
  customReportSaveAction,
  customReportUpdateAction,
  customReportGetAction,
  customReportParseParametersAction,
  customReportSaveFunctionReportAction,
} from "../../../redux/actions/custom-report-action";
import {
  customFunctionValidateAction,
  customFunctionSaveAction,
  customFunctionUpdateAction,
  customFunctionDeleteAction,
} from "../../../redux/actions/custom-function-action";
import { PlayIcon } from "@heroicons/react/24/outline";
import { canCreateAndEditCustomReports } from "../../../utils/custom-report-permissions";

const CustomReportEditor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reportId } = useParams();

  // Check permission - redirect if not allowed
  useEffect(() => {
    if (!canCreateAndEditCustomReports()) {
      navigate("/reports", { replace: true });
    }
  }, [navigate]);

  const [sqlQuery, setSqlQuery] = useState("");
  const [schema, setSchema] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [schemaSearch, setSchemaSearch] = useState("");
  const [results, setResults] = useState({ columns: [], rows: [], rowCount: 0, truncated: false });
  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  // Function modal state
  const [fnModalOpen, setFnModalOpen] = useState(false);
  const [fnModalEdit, setFnModalEdit] = useState(false);
  const [fnModalData, setFnModalData] = useState(null);
  const [fnSaveLoading, setFnSaveLoading] = useState(false);
  const [deleteConfirmFn, setDeleteConfirmFn] = useState(null);

  // Filter config modal state (Save as Report)
  const [filterConfigOpen, setFilterConfigOpen] = useState(false);
  const [filterConfigFn, setFilterConfigFn] = useState(null);
  const [parsedParams, setParsedParams] = useState([]);
  const [filterSaveLoading, setFilterSaveLoading] = useState(false);

  // Get current user ID from JWT
  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.user_id || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchSchema();
    if (reportId) {
      fetchReport(reportId);
    }
  }, [reportId]);

  const fetchSchema = () => {
    setSchemaLoading(true);
    dispatch(
      customReportSchemaAction(
        (response) => {
          const data = response.data || {};
          setSchema(data.tables || data || []);
          setFunctions(data.functions || []);
          setSchemaLoading(false);
        },
        (error) => {
          console.error("Failed to fetch schema:", error);
          setSchemaLoading(false);
        }
      )
    );
  };

  const fetchReport = (id) => {
    dispatch(
      customReportGetAction(
        id,
        (response) => {
          const report = response.data;
          setCurrentReport(report);
          setSqlQuery(report.sql_query);
        },
        (error) => {
          console.error("Failed to fetch report:", error);
        }
      )
    );
  };

  const handleRunQuery = () => {
    if (!sqlQuery.trim()) return;
    setLoading(true);
    setError(null);
    setResults({ columns: [], rows: [], rowCount: 0, truncated: false });

    dispatch(
      customReportExecuteAction(
        { sql_query: sqlQuery },
        (response) => {
          setResults(response.data);
          setLoading(false);
        },
        (errorResponse) => {
          setError(errorResponse?.data?.message || errorResponse?.message || "Query execution failed");
          setLoading(false);
        }
      )
    );
  };

  const handleColumnClick = (tableName, columnName) => {
    const textToInsert = `${tableName}.${columnName}`;
    setSqlQuery((prev) => prev + (prev ? " " : "") + textToInsert);
  };

  const handleFunctionClick = (fn) => {
    const callText = `custom_fns.${fn.db_function_name}()`;
    setSqlQuery((prev) => prev + (prev ? " " : "") + callText);
  };

  const handleSave = ({ name, description, is_shared }) => {
    setSaveLoading(true);
    const action = currentReport ? customReportUpdateAction : customReportSaveAction;
    const payload = currentReport
      ? { report_id: currentReport.report_id, name, description, sql_query: sqlQuery, is_shared }
      : { name, description, sql_query: sqlQuery, is_shared };

    dispatch(
      action(
        payload,
        (response) => {
          setSaveLoading(false);
          setSaveModalOpen(false);
          if (!currentReport && response.data?.report_id) {
            navigate(`/reports/custom-reports/editor/${response.data.report_id}`, { replace: true });
            setCurrentReport({ report_id: response.data.report_id, name, description, is_shared });
          }
          if (currentReport) {
            setCurrentReport({ ...currentReport, name, description, is_shared });
          }
        },
        (error) => {
          console.error("Failed to save report:", error);
          setError(error?.data?.message || error?.message || "Failed to save report");
          setSaveLoading(false);
        }
      )
    );
  };

  // Function handlers
  const handleNewFunction = () => {
    setFnModalEdit(false);
    setFnModalData(null);
    setFnModalOpen(true);
  };

  const handleEditFunction = (fn) => {
    setFnModalEdit(true);
    setFnModalData(fn);
    setFnModalOpen(true);
  };

  const handleDeleteFunction = (fn) => {
    setDeleteConfirmFn(fn);
  };

  const confirmDeleteFunction = () => {
    if (!deleteConfirmFn) return;
    dispatch(
      customFunctionDeleteAction(
        { function_id: deleteConfirmFn.function_id },
        () => {
          setDeleteConfirmFn(null);
          fetchSchema();
        },
        (error) => {
          console.error("Failed to delete function:", error);
          setDeleteConfirmFn(null);
        }
      )
    );
  };

  const handleFnValidate = (data, onSuccess, onError) => {
    dispatch(customFunctionValidateAction(data, onSuccess, onError));
  };

  // Save as Report handler
  const handleSaveAsReport = (fn) => {
    dispatch(
      customReportParseParametersAction(
        { function_definition: fn.function_definition },
        (response) => {
          setParsedParams(response.data || []);
          setFilterConfigFn(fn);
          setFilterConfigOpen(true);
        },
        (error) => {
          console.error("Failed to parse parameters:", error);
          setError(error?.data?.message || error?.message || "Failed to parse function parameters");
        }
      )
    );
  };

  const handleFilterConfigSave = (config) => {
    setFilterSaveLoading(true);
    dispatch(
      customReportSaveFunctionReportAction(
        {
          function_id: filterConfigFn.function_id,
          name: config.name,
          description: config.description,
          is_shared: config.is_shared,
          filters: config.filters,
        },
        () => {
          setFilterSaveLoading(false);
          setFilterConfigOpen(false);
          setFilterConfigFn(null);
          setParsedParams([]);
        },
        (error) => {
          console.error("Failed to save function report:", error);
          setError(error?.data?.message || error?.message || "Failed to save function report");
          setFilterSaveLoading(false);
        }
      )
    );
  };

  const handleFnSave = (payload) => {
    setFnSaveLoading(true);
    const action = fnModalEdit ? customFunctionUpdateAction : customFunctionSaveAction;

    dispatch(
      action(
        payload,
        () => {
          setFnSaveLoading(false);
          setFnModalOpen(false);
          fetchSchema();
        },
        (error) => {
          console.error("Failed to save function:", error);
          setError(error?.data?.message || error?.message || "Failed to save function");
          setFnSaveLoading(false);
        }
      )
    );
  };

  return (
    <InnerLayout
      title={currentReport?.name || "New Custom Report"}
      backBtn={true}
      list={true}
    >
      <div className="flex h-[calc(100vh-120px)]">
        {/* Schema Browser Sidebar */}
        <div className="w-64 flex-shrink-0 border-r border-EAECF0">
          {schemaLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <SchemaBrowser
              tables={schema}
              functions={functions}
              onColumnClick={handleColumnClick}
              onFunctionClick={handleFunctionClick}
              onNewFunction={handleNewFunction}
              onEditFunction={handleEditFunction}
              onDeleteFunction={handleDeleteFunction}
              onSaveAsReport={handleSaveAsReport}
              searchValue={schemaSearch}
              setSearchValue={setSchemaSearch}
              currentUserId={getCurrentUserId()}
            />
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* SQL Editor Section */}
          <div className="p-4 border-b border-EAECF0">
            <SqlEditor
              value={sqlQuery}
              onChange={setSqlQuery}
              height="180px"
              schema={schema}
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <AddButton
                  text="Run Query"
                  icon={<PlayIcon className="w-4 h-4" />}
                  onclkFn={handleRunQuery}
                  loading={loading}
                  isDisabled={!sqlQuery.trim()}
                />
                <DraftButton
                  text="Save"
                  onclkFn={() => setSaveModalOpen(true)}
                  isDisabled={!sqlQuery.trim()}
                />
              </div>
              <ExportDropdown
                data={results.rows}
                columns={results.columns}
                reportName={currentReport?.name || "Custom Report"}
                disabled={results.rows.length === 0}
              />
            </div>
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-F94B4B rounded-md">
                <p className="text-xs text-F51D00">{error}</p>
              </div>
            )}
          </div>

          {/* Results Section */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <CustomReportTable
                columns={results.columns}
                rows={results.rows}
                rowCount={results.rowCount}
                truncated={results.truncated}
              />
            )}
          </div>
        </div>
      </div>

      <SaveReportModal
        isOpen={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={handleSave}
        initialName={currentReport?.name || ""}
        initialDescription={currentReport?.description || ""}
        initialIsShared={currentReport?.is_shared || false}
        isEdit={!!currentReport}
        loading={saveLoading}
      />

      <FunctionEditorModal
        isOpen={fnModalOpen}
        onClose={() => setFnModalOpen(false)}
        onSave={handleFnSave}
        onValidate={handleFnValidate}
        initialData={fnModalData}
        isEdit={fnModalEdit}
        loading={fnSaveLoading}
        schema={schema}
      />

      <FilterConfigModal
        isOpen={filterConfigOpen}
        onClose={() => {
          setFilterConfigOpen(false);
          setFilterConfigFn(null);
          setParsedParams([]);
        }}
        onSave={handleFilterConfigSave}
        parameters={parsedParams}
        initialName={filterConfigFn?.name || ""}
        initialDescription={filterConfigFn?.description || ""}
        loading={filterSaveLoading}
      />

      {/* Delete function confirmation modal */}
      {deleteConfirmFn && (
        <div
          className="modal-background fixed inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteConfirmFn(null)}
        >
          <div
            className="bg-white p-6 rounded-lg w-80 border border-gray-200 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-medium mb-4">Delete Function</h2>
            <p className="text-sm mb-4 text-667085">
              Are you sure you want to delete "{deleteConfirmFn.name}"? This will also drop the
              PostgreSQL function. This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-white border border-D0D5DD text-344054 rounded hover:bg-gray-50"
                onClick={() => setDeleteConfirmFn(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-F94B4B text-white rounded hover:bg-red-600"
                onClick={confirmDeleteFunction}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </InnerLayout>
  );
};

export default CustomReportEditor;
